import React, { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import MoonPhase from "@/components/MoonPhase";
import api from "@/lib/api";

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function Totals({ totals }) {
  const items = [
    { label: "Subjects", value: totals.subjects },
    { label: "Summaries", value: totals.summaries },
    { label: "Quizzes taken", value: totals.quizzes_taken },
    { label: "Quiz Accuracy", value: totals.accuracy != null ? `${totals.accuracy}%` : "—", phase: totals.accuracy ?? null },
  ];
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {items.map((it) => (
        <div key={it.label} className="glass rounded-2xl p-5" data-testid={`total-${it.label.toLowerCase().replace(/\s/g, "-")}`}>
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{it.label}</p>
            {it.phase !== undefined && <MoonPhase accuracy={it.phase} size={20} />}
          </div>
          <p className="mt-4 font-sans text-3xl font-light tracking-tight">{it.value}</p>
        </div>
      ))}
    </div>
  );
}

export default function Progress() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/progress").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  return (
    <DashboardShell>
      <div className="mb-8">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">My Progress</span>
        <h1 className="mt-3 font-sans text-3xl font-light tracking-tight md:text-4xl">Your orbit so far.</h1>
      </div>

      {data && (
        <>
          <Totals totals={data.totals} />

          <h2 className="mb-4 mt-10 font-sans text-xl font-light tracking-tight">Progress by subject</h2>
          {data.subjects.length === 0 ? (
            <p className="text-zinc-400" data-testid="progress-empty">No subjects yet. Create a summary to get started.</p>
          ) : (
            <div className="space-y-4">
              {data.subjects.map((s) => (
                <div key={s.subject} className="glass rounded-2xl p-6" data-testid={`progress-subject-${s.subject}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <MoonPhase accuracy={s.accuracy} size={26} />
                      <div>
                        <p className="font-sans text-lg font-medium text-white">{s.subject}</p>
                        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                          {s.summaries} summaries · {s.quiz_questions} quiz questions · {timeAgo(s.last_studied)}
                        </p>
                      </div>
                    </div>
                    <p className="font-sans text-xl font-light text-white">
                      {s.accuracy != null ? `${s.accuracy}%` : <span className="text-sm text-zinc-500">Not tested</span>}
                    </p>
                  </div>
                  <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-white/5">
                    <div className="h-full rounded-full bg-white/80 transition-all" style={{ width: `${s.accuracy ?? 0}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          <p className="mt-8 text-center font-mono text-xs text-zinc-600" data-testid="progress-footnote">
            Accuracy reflects only quizzes you have actually answered — never overstated.
          </p>
        </>
      )}
    </DashboardShell>
  );
}
